import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
  toast,
} from "@/components/kit";
import {
  useDeleteVariant,
  useVariantDeletePreview,
} from "../api/queries";

export type VariantDeleteDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variantId: string | null;
  variantName: string;
  productId?: string;
  onDeleted?: () => void;
};

export function VariantDeleteDialog({
  open,
  onOpenChange,
  variantId,
  variantName,
  productId,
  onDeleted,
}: VariantDeleteDialogProps) {
  const preview = useVariantDeletePreview(open && variantId ? variantId : null);
  const deleteVariant = useDeleteVariant(productId);

  async function handleDelete() {
    if (!variantId) return;
    const idToDelete = variantId;
    try {
      // ปิด dialog ก่อน เพื่อ disable preview query — กัน refetch 404 หลังลบ
      onOpenChange(false);
      await deleteVariant.mutateAsync(idToDelete);
      toast.success("ລຶບຕົວເລືອກສຳເລັດ");
      onDeleted?.();
    } catch (e) {
      toast.error("ລຶບຕົວເລືອກລົ້ມເຫຼວ", {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  }

  const relations = preview.data?.relations ?? [];
  const hasOrderHistory = preview.data?.hasOrderHistory ?? false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            ລຶບຕົວເລືອກ
          </DialogTitle>
          <DialogDescription>
            ຢືນຢັນລຶບ «{variantName}» — ຂໍ້ມູນທີ່ເຊື່ອມຈະຖືກລຶບພ້ອມກັນ
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {preview.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : preview.isError ? (
            <p className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive text-sm">
              ໂຫຼດຂໍ້ມູນກ່ອນລຶບບໍ່ສຳເລັດ — ລອງໃໝ່ອີກຄັ້ງ
            </p>
          ) : relations.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              ບໍ່ພົບຂໍ້ມູນທີ່ເຊື່ອມກັບຕົວເລືອກນີ້
            </p>
          ) : (
            <ul className="divide-y divide-secondary/15 rounded-xl border border-secondary/20 bg-background/50">
              {relations.map((row) => (
                <li
                  key={row.key}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm"
                >
                  <span>{row.label}</span>
                  <span className="font-mono tabular-nums text-muted-foreground">
                    {row.count}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {hasOrderHistory ? (
            <p className="rounded-xl border border-amber-600/30 bg-amber-500/10 px-3 py-2.5 text-amber-900 text-sm leading-relaxed dark:text-amber-100">
              ມີປະຫວັດອໍເດີ້ — ການລຶບຈະລຶບລາຍການອໍເດີ້ທີ່ເຊື່ອມກັບຕົວເລືອກນີ້ດ້ວຍ
            </p>
          ) : null}

          <p className="text-muted-foreground text-xs leading-relaxed">
            ການລຶບບໍ່ສາມາດຍົກເລີກໄດ້
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
            disabled={deleteVariant.isPending}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="rounded-full"
            isLoading={deleteVariant.isPending}
            disabled={preview.isLoading || preview.isError || !variantId}
            onClick={handleDelete}
          >
            ລຶບຖາວອນ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
